<template>
  <ExpandablePanel
    :is-warning="isFailure"
    class="t-label-item"
  >
    <template #header>
      <div class="header-container">
        <div
          class="name"
          :class="{
            'with-status': hasStatus
          }"
        >
          {{ name }}
        </div>
        <div
          v-if="hasStatus"
          class="status"
        >
          {{ statusValue }}
          <v-icon
            v-if="hasIcon"
            :color="iconColor"
            size="16"
          >
            {{ icon }}
          </v-icon>
        </div>
      </div>
    </template>
    <template
      v-if="hasExpandedContent"
      #default
    >
      <div class="expanded-content">
        <div
          v-for="(item, index) in expandedContent"
          :key="index"
          class="item"
        >
          <div class="item-title">
            {{ item.title }}
          </div>
          <div class="item-content">
            {{ item.content }}
          </div>
        </div>
        <ButtonsContainer
          v-if="hasExpandedContentActions"
          class="actions"
          :fill-width="false"
        >
          <Button
            v-for="(action, index) in expandedContentActions"
            :key="`expanded-content-action-${index}`"
            :to="action.to"
            is-text
            with-outline
            is-medium
            :disabled="action.isDisabled"
            :text="action.title"
            @click="() => action.onClick ? action.onClick() : null"
          />
        </ButtonsContainer>
      </div>
    </template>
  </ExpandablePanel>
</template>
<script>
import ExpandablePanel from '../../atoms/panel/expandable-panel';
import Button from '../../atoms/button/button';
import ButtonsContainer from '../layout/buttons-container';
import variables from '../../../styles/_variables.scss';

import { itemDetailTabSlugs, routeNames, tLabelStatuses } from '../../../constants';

export default {
  name: 'TLabelItem',
  components: {
    ExpandablePanel,
    Button,
    ButtonsContainer,
  },
  props: {
    // label status (e.g. discovered, downloading, downloaded, failed)
    status: { type: String, default: null },
    // downloadStatus is the status of the download process
    downloadStatus: { type: String, default: null },
    asset: { type: Object, default: () => ({}) },
    failureReason: { type: String, default: null },
    readings: { type: Array, default: () => [] },
    currentTemperature: { type: Number, default: null },
    retryIsDisabled: { type: Boolean, default: false },
  },
  computed: {
    name() { return (this.asset || {}).name || 'Unknown Asset'; },
    hasStatus() { return !!this.statusValue; },
    isDownloading() { return this.status === tLabelStatuses.downloading; },
    isSuccess() { return this.status === tLabelStatuses.downloaded; },
    isFailure() { return this.status === tLabelStatuses.failed; },
    isComplete() { return this.isSuccess || this.isFailure; },
    statusValue() {
      if (this.isDownloading) {
        if (this.downloadStatus && this.downloadStatus.length > 0) {
          const value = this.downloadStatus.replaceAll('_', ' ');
          // capitalise the first letter of the download status
          return `${value.charAt(0).toUpperCase()}${value.slice(1).toLowerCase()}`;
        }

        return 'Downloading';
      }

      if (this.isSuccess) { return 'Success'; }
      if (this.isFailure) { return 'Failed'; }

      return '';
    },
    icon() {
      if (this.isSuccess) { return 'mdi-check'; }
      if (this.isFailure) { return 'mdi-close-circle-outline'; }

      return '';
    },
    hasIcon() { return !!this.icon; },
    iconColor() {
      if (this.isSuccess) { return variables.tealBright; }
      if (this.isFailure) { return variables.errorLight; }

      return '';
    },
    expandedContent() {
      if (this.isFailure) {
        return [{
          title: 'Detail',
          content: this.failureReason || 'No details available',
        }];
      }

      if (this.isSuccess) {
        const items = [];
        const readings = this.readings || [];
        if (readings.length > 0) {
          items.push({
            title: 'Number of readings',
            content: readings.length,
          });

          // temperature range
          const temperatures = readings.map((r) => r.degreesCelsius);

          if (temperatures.length > 0) {
            items.push({
              title: 'Temperature range',
              content: `${Math.min(...temperatures)}°C - ${Math.max(...temperatures)}°C`,
            });
          }

          items.push({
            title: 'Earliest reading at',
            content: readings[0].timestamp,
          });

          items.push({
            title: 'Latest reading at',
            content: readings[readings.length - 1].timestamp,
          });
        }

        if (typeof this.currentTemperature === 'number') {
          items.push({
            title: 'Current temperature',
            content: `${this.currentTemperature}°C`,
          });
        }

        return items;
      }

      return null;
    },
    expandedContentActions() {
      const actions = [];
      if (this.isComplete && this.asset && this.asset.id) {
        actions.push({
          title: 'View Asset',
          to: {
            name: routeNames.assets.detail,
            params: { id: this.asset.id, resetTemperatureData: true },
            query: {
              tab: itemDetailTabSlugs.temperature,
            },
          },
        });

        if (this.isFailure) {
          actions.push({
            title: 'Retry',
            onClick: () => {
              this.$emit('retry');
            },
            isDisabled: this.retryIsDisabled,
          });
        }
      }

      return actions;
    },
    hasExpandedContentActions() { return this.expandedContentActions.length > 0; },
    hasExpandedContent() {
      return (!!this.expandedContent && this.expandedContent.length > 0)
        || this.hasExpandedContentActions;
    },
  },
};
</script>
<style lang="scss" scoped>
@import '@/styles/_variables.scss';
@import '@/styles/_breakpoints.scss';

.t-label-item {
  margin-bottom: 10px;
}

.header-container {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  height: 100%;
  .name {
    text-overflow: ellipsis;
    white-space: nowrap;
    width: 70%;
    max-width: 260px;
    overflow: hidden;
    @include mq($from: md) {
      max-width: 800px;
    }
    &.with-status {
      max-width: 180px;

      @include mq($from: md) {
        max-width: 620px;
      }
    }
  }
  .status {
    // width: 100px;
    width: 30%;
    margin-left: 10px;
    padding-left: 10px;
    border-left: 1px solid #D9D9D9;
    height: 75%;
    display: flex;
    flex-direction: row;
    justify-content: flex-start; // left align the text
    align-items: center;
    font-size: 0.75rem;

    @include mq($from: md) {
      width: 220px;
    }

    i {
      margin-left: 2px;
      margin-bottom: 2px;
    }
  }
}
.expanded-content {
  .item {
    font-size: 0.875rem;
    margin-bottom: 10px;
    text-align: left;
    .item-title {
      color: $gray1;
    }
    .item-content {
      padding-left: 5px;
    }
  }
  .actions {
    margin-top: 20px;
    display: flex;
    flex-direction: row;
    justify-content: center;
  }
}
</style>
